import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftCircleIcon } from '@heroicons/react/24/outline'

/**
 * Sticky top banner shown on a destination editor page when the
 * user arrived from either the WPS Preview or the PQR Preview via
 * the field-navigation flow.
 *
 * Why one component for two flows: every WPS source form ships a
 * sticky `?returnTo=` banner. When this used to be two parallel
 * components (`PqrReturnBanner` + `WpsReturnBanner`) destination
 * pages had to mount both side-by-side and trust each one to no-op
 * — but the PQR variant rendered unconditionally and the WPS
 * variant only rendered for `/wps` paths, so an inbound click from
 * the WPS Preview painted the wrong header on every destination.
 * Unifying the banner removes the failure mode at the source.
 *
 * Renders nothing when `returnTo` is null/empty so destination
 * pages can drop it in unconditionally.
 */

interface Props {
  /** Decoded URL to navigate back to. Banner is hidden when null/empty. */
  returnTo: string | null
  /**
   * Short human label describing what the user came here to edit,
   * e.g. "Base Material" or "Clamp Information". Optional.
   */
  contextLabel?: string
}

export type EditingReturnSource = 'wps' | 'pqr'

/**
 * Identify whether a `returnTo` URL points at the WPS Preview or
 * the PQR Preview. WPS preview URLs end in `/wps` (optionally with
 * a query string); PQR preview URLs include `/pqr/preview` or
 * `/pqr-preview`. Everything else defaults to PQR — the historical
 * behaviour for unparseable URLs.
 */
export function detectEditingReturnSource(returnTo: string): EditingReturnSource {
  const path = returnTo.split('?')[0] ?? ''
  if (/\/wps(?:\/|$)/.test(path)) return 'wps'
  return 'pqr'
}

const SOURCE_COPY: Record<EditingReturnSource, { editingFor: string; backTo: string; testId: string }> = {
  wps: {
    editingFor: 'Editing for WPS Preview',
    backTo: 'Back to WPS Preview',
    testId: 'wps-return-banner'
  },
  pqr: {
    editingFor: 'Editing for PQR Preview',
    backTo: 'Back to PQR Preview',
    testId: 'pqr-return-banner'
  }
}

export default function EditingReturnBanner({ returnTo, contextLabel }: Props) {
  const navigate = useNavigate()
  if (!returnTo) return null
  const source = detectEditingReturnSource(returnTo)
  const copy = SOURCE_COPY[source]

  return (
    <div
      className="sticky top-0 z-40 -mx-6 mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-hero-red-30 bg-hero-red-10 px-6 py-2 text-sm text-hero-red-90"
      role="status"
      data-testid={copy.testId}
      data-return-source={source}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-hero-red-70">
          {copy.editingFor}
        </span>
        {contextLabel ? (
          <>
            <span className="text-xs font-semibold uppercase tracking-wide text-hero-red-70">
              •
            </span>
            <span className="truncate text-xs font-medium text-hero-red-90">
              {contextLabel}
            </span>
          </>
        ) : null}
      </div>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-lg bg-hero-red px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-150 hover:bg-hero-red-60 focus:outline-none focus:ring-2 focus:ring-hero-red focus:ring-offset-2"
        onClick={() => navigate(returnTo)}
      >
        <ArrowLeftCircleIcon className="h-4 w-4" />
        {copy.backTo}
      </button>
    </div>
  )
}

/**
 * Convenience used by `PageHeader.backTo.label` on destination
 * pages so the inline back-arrow caption matches the banner copy
 * without each page re-implementing the URL inspection.
 */
export function backToLabelFor(returnTo: string | null | undefined): string {
  if (!returnTo) return 'Back'
  const source = detectEditingReturnSource(returnTo)
  return source === 'wps' ? 'Back to WPS Preview' : 'Back to PQR Preview'
}
